import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { UserCog, Plus, Edit3, X, Save, Eye, EyeOff, AlertCircle, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import VolverOnboardingBtn from '../../components/VolverOnboardingBtn';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PER_PAGE = 10;

const toTitleCase = (s) => s ? s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase()) : '';

const formatRutOnChange = (raw) => {
  const clean = raw.replace(/[^0-9kK]/g, '').toUpperCase();
  if (clean.length <= 1) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
};

const formatRut = (rut) => {
  if (!rut || rut.startsWith('ESE-')) return '-';
  const clean = rut.replace(/[.\-]/g, '');
  if (clean.length < 2) return clean;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  return `${body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv.toUpperCase()}`;
};

const validarRut = (rut) => {
  const clean = rut.replace(/[.\-]/g, '').trim().toUpperCase();
  if (clean.length < 2) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d+$/.test(body)) return false;
  let total = 0, factor = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    total += parseInt(body[i]) * factor;
    factor = factor < 7 ? factor + 1 : 2;
  }
  const remainder = 11 - (total % 11);
  const expected = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);
  return dv === expected;
};

const emptyForm = { nombre: '', apellido: '', rut: '', email: '', password: '', cargo: '' };

export default function EseUsuarios() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [usuarios, setUsuarios] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rutError, setRutError] = useState('');

  const fetchUsuarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ese/usuarios`, { headers });
      setUsuarios(res.data || []);
    } catch { toast.error('Error al cargar usuarios'); }
  }, [token]);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);
  useEffect(() => { setPage(1); }, [search]);

  const filtered = usuarios.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    const cleanQ = q.replace(/[.\-]/g, '');
    return (u.nombre || '').toLowerCase().includes(q)
      || (u.apellido || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.cargo || '').toLowerCase().includes(q)
      || (u.rut || '').replace(/[.\-]/g, '').toLowerCase().includes(cleanQ);
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowPassword(false);
    setRutError('');
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ nombre: u.nombre || '', apellido: u.apellido || '', rut: u.rut || '', email: u.email || '', password: '', cargo: u.cargo || '' });
    setRutError('');
    setShowModal(true);
  };

  const handleRutChange = (val) => {
    const formatted = formatRutOnChange(val);
    setForm({ ...form, rut: formatted });
    const clean = formatted.replace(/[.\-]/g, '').trim();
    if (clean.length >= 2) {
      setRutError(validarRut(formatted) ? '' : 'Digito verificador invalido');
    } else {
      setRutError('');
    }
  };

  const handleSave = async () => {
    if (!form.nombre || !form.email) { toast.error('Nombre y email son obligatorios'); return; }
    if (!editing && !form.password) { toast.error('La contrasena es obligatoria para nuevos usuarios'); return; }
    if (form.rut && form.rut.trim() && !form.rut.startsWith('ESE-')) {
      if (!validarRut(form.rut)) { toast.error('RUT invalido: el digito verificador no coincide'); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/ese/usuarios/${editing.id}`, {
          nombre: form.nombre, apellido: form.apellido, rut: form.rut, email: form.email, activo: true, cargo: form.cargo
        }, { headers });
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/ese/usuarios`, form, { headers });
        toast.success('Usuario creado');
      }
      setShowModal(false);
      fetchUsuarios();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="ese-usuarios">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <VolverOnboardingBtn user={user} />
          <h1 className="text-2xl font-bold text-slate-900">Usuarios del Sistema</h1>
        </div>
        <button onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          data-testid="crear-usuario-btn">
          <Plus className="w-4 h-4" />Nuevo Usuario
        </button>
      </div>

      <p className="text-sm text-slate-500">Gestione las cuentas de acceso al portal de su empresa.</p>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, RUT, email o cargo..."
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm bg-white" data-testid="search-usuario" />
        </div>
        <span className="text-sm text-slate-400 shrink-0">{filtered.length} usuarios</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-slate-500 font-medium">RUT</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Nombre</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Cargo</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Email</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Estado</th>
              <th className="text-right px-4 py-3 text-slate-500 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(u => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 font-mono text-xs">{formatRut(u.rut)}</td>
                <td className="px-4 py-3 font-medium text-slate-800">{toTitleCase(u.nombre)} {toTitleCase(u.apellido)}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{toTitleCase(u.cargo || '') || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">{(u.email || '').toLowerCase()}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {u.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(u)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 bg-blue-50 rounded-lg text-xs hover:bg-blue-100 transition-colors"
                    data-testid={`edit-usuario-${u.id}`}>
                    <Edit3 className="w-3 h-3" />Editar
                  </button>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                <UserCog className="w-10 h-10 mx-auto mb-2 opacity-30" />
                No hay usuarios registrados
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
          <span className="text-sm text-slate-500">{(page - 1) * PER_PAGE + 1}-{Math.min(page * PER_PAGE, filtered.length)} de {filtered.length}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let p;
              if (totalPages <= 5) p = i + 1;
              else if (page <= 3) p = i + 1;
              else if (page >= totalPages - 2) p = totalPages - 4 + i;
              else p = page - 2 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium ${p === page ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{p}</button>
              );
            })}
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {showModal && createPortal(
        <div style={{ backgroundColor: "rgba(0,0,0,0.5)" }} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">RUT *</label>
                <input value={form.rut} onChange={e => handleRutChange(e.target.value)}
                  placeholder="12.345.678-9"
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${rutError ? 'border-red-300 bg-red-50/50' : 'border-slate-200'}`}
                  data-testid="input-usuario-rut" />
                {rutError && (
                  <p className="flex items-center gap-1 mt-1 text-xs text-red-500">
                    <AlertCircle className="w-3 h-3" />{rutError}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Nombre *</label>
                  <input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-usuario-nombre" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Apellido</label>
                  <input value={form.apellido} onChange={e => setForm({ ...form, apellido: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-usuario-apellido" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Cargo</label>
                <input value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })}
                  placeholder="Administrador, Prevencionista, etc."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-usuario-cargo" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="input-usuario-email" />
              </div>
              {!editing && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Contrasena *</label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg text-sm" data-testid="input-usuario-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSave} disabled={saving || !!rutError}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                data-testid="save-usuario-btn">
                <Save className="w-4 h-4" />{saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
